import type { DayOfWeek, OpeningHoursSpecification } from './studio';

export const OPENING_HOURS_WEEKDAYS: DayOfWeek[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const DAY_CALENDAR_OFFSET: Record<DayOfWeek, number> = {
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
  Sunday: 7,
};

type DayShifts = { opens: string; closes: string }[];

function dayKeysFromSpec(row: OpeningHoursSpecification): DayOfWeek[] {
  return typeof row.dayOfWeek === 'string' ? [row.dayOfWeek] : [...row.dayOfWeek];
}

function shiftSignature(shifts: DayShifts): string {
  return shifts.map((shift) => `${shift.opens}-${shift.closes}`).join(',');
}

function formatDayLabel(day: DayOfWeek, locale: string, weekday: 'short' | 'long' = 'short'): string {
  return new Intl.DateTimeFormat(locale, { weekday }).format(
    new Date(2024, 0, DAY_CALENDAR_OFFSET[day]),
  );
}

function formatShiftTimes(shifts: DayShifts): string {
  return shifts.map((shift) => `${shift.opens}–${shift.closes}`).join(', ');
}

/** Expand Schema.org rows into Mon→Sun shift lists (empty = closed / omitted). */
export function groupOpeningHoursShiftsByDay(
  rows: OpeningHoursSpecification[] | null | undefined,
): Record<DayOfWeek, DayShifts> {
  const grouped = Object.fromEntries(
    OPENING_HOURS_WEEKDAYS.map((day) => [day, [] as DayShifts]),
  ) as Record<DayOfWeek, DayShifts>;

  for (const row of rows ?? []) {
    for (const day of dayKeysFromSpec(row)) {
      if (!OPENING_HOURS_WEEKDAYS.includes(day)) continue;
      grouped[day].push({ opens: row.opens, closes: row.closes });
    }
  }
  return grouped;
}

/**
 * Locale-aware opening-hours text for footer / legal tokens / Zone 4.
 * Groups consecutive open days that share the same shifts (e.g. `Di–Sa 12:00–23:00`).
 * Default separator is newline so footer `parseHoursSchedule` builds a 2-column table.
 */
export function formatOpeningHoursSchedule(
  rows: OpeningHoursSpecification[] | null | undefined,
  locale: string,
  options?: { separator?: string; weekday?: 'short' | 'long' },
): string {
  const grouped = groupOpeningHoursShiftsByDay(rows);
  const weekday = options?.weekday ?? 'short';
  const separator = options?.separator ?? '\n';
  const lines: string[] = [];

  let index = 0;
  while (index < OPENING_HOURS_WEEKDAYS.length) {
    const startDay = OPENING_HOURS_WEEKDAYS[index]!;
    const startShifts = grouped[startDay];
    if (startShifts.length === 0) {
      index += 1;
      continue;
    }

    const signature = shiftSignature(startShifts);
    let endIndex = index;
    while (endIndex + 1 < OPENING_HOURS_WEEKDAYS.length) {
      const nextDay = OPENING_HOURS_WEEKDAYS[endIndex + 1]!;
      const nextShifts = grouped[nextDay];
      if (nextShifts.length === 0) break;
      if (shiftSignature(nextShifts) !== signature) break;
      endIndex += 1;
    }

    const endDay = OPENING_HOURS_WEEKDAYS[endIndex]!;
    const rangeSep = weekday === 'long' ? ' – ' : '–';
    const dayLabel =
      startDay === endDay
        ? formatDayLabel(startDay, locale, weekday)
        : `${formatDayLabel(startDay, locale, weekday)}${rangeSep}${formatDayLabel(endDay, locale, weekday)}`;
    lines.push(`${dayLabel} ${formatShiftTimes(startShifts)}`);
    index = endIndex + 1;
  }

  return lines.join(separator);
}
